// KPI and chart sizing tokens, ported from Artyom's summer dashboard tokens
// (vendor/compliance-dashboard/tokens/dashboardTokens.js, widget.kpi and widget.chart).
// Pure data and pure functions: the renderer (KpiWidget, WidgetFrame, chartOption) reads them,
// so the numbers live in one place instead of being copied into each component.

export const KPI_VALUE_FIT = {
  maxPx: 104, // largest the auto-fit value can grow
  minPx: 12, // smallest the value shrinks to
  heightReservePx: 36, // card chrome taken off the card height before the ratio
  heightRatio: 0.72, // value size ~ (cardHeight - 36) x this
  titleRatio: 0.3, // label size ~ height-fit value size x this
  titleMinPx: 8,
  titleMaxPx: 16,
  compactMaxPx: 32, // kpiLayout "compact" keeps main's cap
  // Summer KpiStat renders calc(valueSize * --ds-font-scale / 1.12): "Large" (1.12) is the
  // auto-fit size and Normal / Compact step down from it. A 96px card: (96 - 36) x 0.72 = 43,
  // shown at 43 / 1.12 = 38px on Normal.
  fontScaleTop: 1.12,
}

// Summer KPI composition: label, then value, then an optional sublabel or delta, 2px apart.
export const KPI_COMPOSITION = {
  gapPx: 2,
  valueLineHeight: 1.1,
  labelLineHeight: 1.25,
  labelMaxLines: 2,
  targetReservePx: 66, // the target bar or ring under the value (main's reserve)
  // The sublabel carries the title's caveat ("83.5% eligible"): muted 11px under the value.
  // Below either size it is dropped first, before the value shrinks; the PDF always keeps it.
  sublabelPx: 11,
  sublabelMinHeightPx: 84,
  sublabelMinWidthPx: 120,
  // A delta line under the value needs this much more height before the sublabel fits too.
  deltaReservePx: 18,
  // How far the sublabel may sit into the body's bottom padding.
  sublabelBleedPx: 8,
  // On screen the sublabel stays only while the value keeps at least 80% of its size without it.
  sublabelKeepRatio: 0.8,
}

// Chart modes (summer widget.chart). Main's thresholds already matched; they now read from here.
export const CHART_SIZE = {
  compact: { widthPx: 340, heightPx: 220 },
  tiny: { widthPx: 260, heightPx: 160 },
  // A line draws a dot on every point only up to 24 points; a daily series marks its last point only.
  line: { widthMin: 1.25, widthMax: 2.5, markerSize: 4, markerSizeCompact: 3, maxMarkerPoints: 24 },
  maxSeriesWithMarkers: 4,
  bar: { maxWidthPx: 48, comboMaxWidthPx: 36, compactMaxWidthPx: 24 },
  donut: { donut: ["36%", "68%"], pie: [0, "68%"] },
}

// Chart text at 100% zoom. Axis labels never below 11px, the legend 12px (summer chart chrome
// was 9-11px, which read as ~7px at the editor's 66% zoom).
export const CHART_TEXT = {
  axisPx: 12,
  axisCompactPx: 11,
  legendPx: 12,
  // A line chart at least this tall keeps its value axis even in the "tiny" mode.
  lineAxisMinHeightPx: 100,
}

// Chart text at the Design font size (backlog #18): axis and legend px times the font scale, never below 10px.
export function chartTextSizes(compact, fontScale = 1) {
  const scale = Number(fontScale) > 0 ? Number(fontScale) : 1
  const px = (value) => Math.max(10, Math.round(value * scale))
  return { axis: px(compact ? CHART_TEXT.axisCompactPx : CHART_TEXT.axisPx), legend: px(CHART_TEXT.legendPx) }
}

export function chartSizeMode(width, height) {
  const w = Number(width || 0)
  const h = Number(height || 0)
  const tiny = w < CHART_SIZE.tiny.widthPx || h < CHART_SIZE.tiny.heightPx
  const compact = tiny || w < CHART_SIZE.compact.widthPx || h < CHART_SIZE.compact.heightPx
  return { compact, tiny }
}

export function lineShowsMarkers(points, seriesCount, tiny) {
  return !tiny && seriesCount <= CHART_SIZE.maxSeriesWithMarkers && Number(points || 0) <= CHART_SIZE.line.maxMarkerPoints
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

// The size the value would have from the card height alone (summer valueFit).
export function kpiHeightFitSize(cardHeight) {
  const fit = KPI_VALUE_FIT
  return clamp(Math.round((Number(cardHeight || 0) - fit.heightReservePx) * fit.heightRatio), fit.minPx, fit.maxPx)
}

// The label tracks the height-fit size, not the width-shrunk one, so a long number only
// shrinks its digits and the label stays the size the card height allows (summer rule).
export function kpiLabelSize(cardHeight, fontScale = 1) {
  const fit = KPI_VALUE_FIT
  const size = clamp(Math.round(kpiHeightFitSize(cardHeight) * fit.titleRatio), fit.titleMinPx, fit.titleMaxPx)
  return Math.max(fit.titleMinPx, Math.round(size * (Number(fontScale) || 1)))
}

/** @param {{ cardWidth?: number, cardHeight?: number, hasSublabel?: boolean, hasTarget?: boolean, hasDelta?: boolean, printing?: boolean }} input */
export function kpiShowsSublabel({ cardWidth, cardHeight, hasSublabel, hasTarget, hasDelta, printing }) {
  if (!hasSublabel) return false
  // The PDF never drops a caveat: the value shrinks instead.
  if (printing) return true
  const minHeight =
    KPI_COMPOSITION.sublabelMinHeightPx +
    (hasTarget ? KPI_COMPOSITION.targetReservePx : 0) +
    (hasDelta ? KPI_COMPOSITION.deltaReservePx : 0)
  return Number(cardHeight || 0) >= minHeight && Number(cardWidth || 0) >= KPI_COMPOSITION.sublabelMinWidthPx
}

// Lines the sublabel may take: one on a short card, two from 130px.
export function kpiSublabelLines(cardHeight) {
  return Number(cardHeight || 0) >= 130 ? 2 : 1
}

/**
 * Value size for a KPI. Keeps main's real-width fit (textEm = the measured width of the whole
 * displayed string, unit included, at 1px) and clamps it to the summer 12-104px range by the
 * widget height as well as the width.
 * The body height only stops the value from clipping (summer has no other cap).
 * @param {{ cardHeight: number, bodyWidth: number, bodyHeight?: number, bodyBoxHeight?: number, textEm?: number, chars: number,
 *   hasTarget?: boolean, sublabelPx?: number, layout?: string, fontScale?: number }} input
 */
export function kpiValueSize(input) {
  const fit = KPI_VALUE_FIT
  const heightFit = kpiHeightFitSize(input.cardHeight)
  const reserve = (input.hasTarget ? KPI_COMPOSITION.targetReservePx : 0) + (input.sublabelPx || 0)
  const bodyFit = input.bodyHeight
    ? Math.floor(Math.max(0, input.bodyHeight - reserve) / KPI_COMPOSITION.valueLineHeight)
    : Infinity
  const em = input.textEm || Math.max(3, input.chars || 0) * 0.62
  const widthFit = Math.floor(Math.max(0, input.bodyWidth) / em)
  const cap = input.layout === "compact" ? fit.compactMaxPx : fit.maxPx
  const scale = (Number(input.fontScale) || 1) / fit.fontScaleTop
  return clamp(Math.floor(Math.min(heightFit * scale, bodyFit, widthFit, cap)), fit.minPx, fit.maxPx)
}
